import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plus, Trash, PaperPlaneTilt, ChartBar, Clock, CheckCircle, Star, TextT, ListChecks, Lock, XCircle, ClipboardText, PencilSimple,
} from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const Q_TYPES = [
  { value: 'single_choice', label_ar: 'اختيار متعدد', label_en: 'Single Choice', icon: ListChecks },
  { value: 'rating', label_ar: 'تقييم نجمي', label_en: 'Star Rating', icon: Star },
  { value: 'text', label_ar: 'إجابة قصيرة', label_en: 'Short Text', icon: TextT },
];

const emptyQuestion = () => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  type: 'single_choice',
  title_ar: '',
  title_en: '',
  options: ['', ''],
  scale: 5,
  required: true,
});

export default function AdminSurveys() {
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = create mode

  // Builder state
  const [titleAr, setTitleAr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [descAr, setDescAr] = useState('');
  const [descEn, setDescEn] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [confidentialityNotice, setConfidentialityNotice] = useState(true);
  const [audienceMode, setAudienceMode] = useState('all');
  const [audienceDepts, setAudienceDepts] = useState([]);
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => { load(); loadDepartments(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/surveys`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل تحميل الاستبيانات', 'Failed to load'));
    } finally { setLoading(false); }
  };

  const loadDepartments = async () => {
    try {
      const r = await axios.get(`${API}/departments`, { withCredentials: true });
      setDepartments(r.data || []);
    } catch { /* non-fatal */ }
  };

  const resetBuilder = () => {
    setTitleAr(''); setTitleEn(''); setDescAr(''); setDescEn('');
    setAnonymous(true); setConfidentialityNotice(true);
    setAudienceMode('all'); setAudienceDepts([]);
    setQuestions([emptyQuestion()]);
    setEditingId(null);
  };

  const startEditDraft = (s) => {
    setEditingId(s.id);
    setTitleAr(s.title_ar || '');
    setTitleEn(s.title_en || '');
    setDescAr(s.description_ar || '');
    setDescEn(s.description_en || '');
    setAnonymous(!!s.anonymous);
    setConfidentialityNotice(!!s.confidentiality_notice);
    const aud = s.audience || { mode: 'all' };
    setAudienceMode(aud.mode || 'all');
    setAudienceDepts(aud.departments || []);
    const qs = (s.questions || []).map((q) => ({
      id: q.id || `q_${Math.random().toString(36).slice(2, 9)}`,
      title_ar: q.title_ar || '',
      title_en: q.title_en || '',
      type: q.type || 'rating',
      required: !!q.required,
      scale: q.scale || 5,
      options: Array.isArray(q.options) ? [...q.options] : [],
    }));
    setQuestions(qs.length ? qs : [emptyQuestion()]);
    setDialogOpen(true);
  };

  const addQuestion = () => setQuestions((p) => [...p, emptyQuestion()]);
  const removeQuestion = (idx) => setQuestions((p) => p.filter((_, i) => i !== idx));
  const updateQuestion = (idx, patch) => setQuestions((p) => p.map((q, i) => (i === idx ? { ...q, ...patch } : q)));

  const saveSurvey = async () => {
    if (!titleAr.trim()) return toast.error(T('العنوان مطلوب', 'Title required'));
    if (!questions.length) return toast.error(T('أضف سؤالاً واحداً على الأقل', 'Add at least one question'));
    for (const q of questions) {
      if (!q.title_ar.trim()) return toast.error(T('كل سؤال يحتاج عنواناً', 'Every question needs a title'));
      if (q.type === 'single_choice') {
        const nonEmpty = (q.options || []).filter((o) => o.trim());
        if (nonEmpty.length < 2) return toast.error(T(`أضف خيارين على الأقل في السؤال "${q.title_ar}"`, 'At least 2 options'));
      }
    }
    setSaving(true);
    try {
      const payload = {
        title_ar: titleAr, title_en: titleEn,
        description_ar: descAr, description_en: descEn,
        anonymous,
        confidentiality_notice: confidentialityNotice,
        audience: {
          mode: audienceMode,
          departments: audienceMode === 'departments' ? audienceDepts : [],
        },
        questions: questions.map((q) => ({
          ...q,
          options: (q.options || []).filter((o) => o.trim()),
        })),
      };
      if (editingId) {
        await axios.put(`${API}/surveys/${editingId}`, payload, { withCredentials: true });
        toast.success(T('تم تحديث المسودة', 'Draft updated'));
      } else {
        await axios.post(`${API}/surveys`, payload, { withCredentials: true });
        toast.success(T('تم إنشاء الاستبيان كمسودة', 'Survey saved as draft'));
      }
      setDialogOpen(false);
      resetBuilder();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الحفظ', 'Save failed'));
    } finally { setSaving(false); }
  };

  const describeAudience = (aud) => {
    const mode = aud?.mode || 'all';
    if (mode === 'all') return T('جميع الموظفين النشطين', 'all active employees');
    if (mode === 'departments') {
      const vals = aud.departments || [];
      const labels = vals.map((v) => {
        const d = departments.find((x) => (x.value || x.label_ar) === v);
        if (!d) return v;
        return isAr ? (d.label_ar || d.label_en || v) : (d.label_en || d.label_ar || v);
      });
      return T(`موظفي الأقسام: ${labels.join('، ')}`, `employees in: ${labels.join(', ')}`);
    }
    if (mode === 'positions') {
      return T(`شاغلي المناصب: ${(aud.positions || []).join('، ')}`, `positions: ${(aud.positions || []).join(', ')}`);
    }
    if (mode === 'users') {
      return T(`قائمة محددة (${(aud.user_ids || []).length} موظف)`, `specific list (${(aud.user_ids || []).length} users)`);
    }
    return T('الجمهور المحدد', 'the selected audience');
  };

  const sendSurvey = async (s) => {
    const id = s.id;
    const scope = describeAudience(s.audience);
    if (!window.confirm(T(
      `سيتم إرسال الاستبيان إلى ${scope} عبر: التطبيق + Push + البريد. تابع؟`,
      `The survey will be sent to ${scope} via in-app + push + email. Continue?`,
    ))) return;
    setSendingId(id);
    try {
      const r = await axios.post(`${API}/surveys/${id}/send`, {}, { withCredentials: true });
      toast.success(T(
        `تم الفتح للـ${r.data.recipient_count} موظف · إرسال ${r.data.email_total || 0} بريد في الخلفية`,
        `Opened for ${r.data.recipient_count} employees · ${r.data.email_total || 0} emails being sent in background`,
      ));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الإرسال', 'Send failed'));
    } finally { setSendingId(null); }
  };

  const closeSurvey = async (id) => {
    if (!window.confirm(T('إغلاق الاستبيان نهائياً؟', 'Close survey permanently?'))) return;
    try {
      await axios.post(`${API}/surveys/${id}/close`, {}, { withCredentials: true });
      toast.success(T('تم الإغلاق', 'Closed'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || T('فشل', 'Failed')); }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm(T('حذف المسودة؟', 'Delete draft?'))) return;
    try {
      await axios.delete(`${API}/surveys/${id}`, { withCredentials: true });
      toast.success(T('تم الحذف', 'Deleted'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || T('فشل', 'Failed')); }
  };

  const statusBadge = (s) => {
    const map = {
      draft: { ar: 'مسودة', en: 'Draft', cls: 'bg-gray-100 text-gray-700' },
      open: { ar: 'مفتوح', en: 'Open', cls: 'bg-emerald-100 text-emerald-800' },
      closed: { ar: 'مغلق', en: 'Closed', cls: 'bg-rose-100 text-rose-800' },
    };
    const info = map[s] || map.draft;
    return <Badge className={`${info.cls} border-0`}>{T(info.ar, info.en)}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="admin-surveys-page">
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <ClipboardText size={28} weight="duotone" />
              {T('إدارة الاستبيانات', 'Surveys Management')}
            </h1>
            <p className="text-sm text-[#655dc6]/70 mt-1">
              {T('أنشئ استبيانات قصيرة من إدارة الجودة مع إرسال فوري عبر التطبيق والبريد', 'Create short quality surveys with instant multi-channel delivery')}
            </p>
          </div>
          <Button
            onClick={() => { resetBuilder(); setDialogOpen(true); }}
            className="bg-[#655dc6] hover:bg-[#564db5] text-white"
            data-testid="create-survey-btn"
          >
            <Plus size={18} weight="bold" className="me-1" />
            {T('استبيان جديد', 'New Survey')}
          </Button>
        </div>

      {loading ? (
        <div className="text-center text-[#655dc6]/60 py-8">{T('جار التحميل...', 'Loading...')}</div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-[#655dc6]/60">
            <ChartBar size={48} className="mx-auto mb-3 opacity-40" />
            <p>{T('لا توجد استبيانات بعد. أنشئ أول استبيان من الزر أعلاه.', 'No surveys yet. Create your first one.')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.map((s) => (
            <Card key={s.id} data-testid={`survey-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(s.status)}
                      <h3 className="text-base md:text-lg font-semibold text-[#655dc6] dark:text-violet-300 truncate">
                        {isAr ? s.title_ar : (s.title_en || s.title_ar)}
                      </h3>
                      {s.anonymous && (
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">
                          <Lock size={10} className="me-0.5" /> {T('مجهول', 'Anon')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-[#655dc6]/60 mt-1 flex flex-wrap gap-3">
                      <span>{T('أسئلة', 'Questions')}: <strong>{s.questions?.length || 0}</strong></span>
                      <span>{T('مرسل', 'Sent to')}: <strong>{s.recipient_count || 0}</strong></span>
                      <span>{T('ردود', 'Responses')}: <strong className="text-emerald-700">{s.response_count || 0}</strong></span>
                      {s.recipient_count > 0 && (
                        <span>{T('نسبة الرد', 'Rate')}: <strong>{Math.round(100 * (s.response_count || 0) / s.recipient_count)}%</strong></span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => sendSurvey(s)}
                          disabled={sendingId === s.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`send-${s.id}`}
                        >
                          <PaperPlaneTilt size={14} weight="bold" className="me-1" />
                          {sendingId === s.id ? T('إرسال...', 'Sending...') : T('إرسال', 'Send')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditDraft(s)}
                          data-testid={`edit-${s.id}`}
                        >
                          <PencilSimple size={14} className="me-1" />
                          {T('تعديل', 'Edit')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteDraft(s.id)}>
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                    {s.status === 'open' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/admin/surveys/${s.id}/results`)}>
                          <ChartBar size={14} className="me-1" /> {T('النتائج', 'Results')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => closeSurvey(s.id)}>
                          <XCircle size={14} className="me-1" /> {T('إغلاق', 'Close')}
                        </Button>
                      </>
                    )}
                    {s.status === 'closed' && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/surveys/${s.id}/results`)}>
                        <ChartBar size={14} className="me-1" /> {T('النتائج', 'Results')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Builder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir={direction}>
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {editingId
                ? T('تعديل مسودة الاستبيان', 'Edit Draft Survey')
                : T('استبيان جديد - إدارة الجودة', 'New Survey - Quality Management')}
            </DialogTitle>
            <DialogDescription>
              {T('استبيان قصير 2-3 دقائق. اختر الجمهور، أضف الأسئلة، ثم احفظ واضغط إرسال.', '2-3 minute short survey. Pick audience, add questions, save & send.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>{T('العنوان (عربي)', 'Title (AR)')} *</Label>
                <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} data-testid="title-ar" />
              </div>
              <div>
                <Label>{T('العنوان (إنجليزي)', 'Title (EN)')}</Label>
                <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} dir="ltr" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>{T('وصف (عربي)', 'Description (AR)')}</Label>
                <Textarea rows={2} value={descAr} onChange={(e) => setDescAr(e.target.value)} />
              </div>
              <div>
                <Label>{T('وصف (إنجليزي)', 'Description (EN)')}</Label>
                <Textarea rows={2} value={descEn} onChange={(e) => setDescEn(e.target.value)} dir="ltr" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 p-3 bg-[#F8F7FC] dark:bg-slate-800 rounded">
              <div>
                <Label>{T('الجمهور', 'Audience')}</Label>
                <Select value={audienceMode} onValueChange={setAudienceMode}>
                  <SelectTrigger data-testid="audience-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{T('جميع الموظفين', 'All employees')}</SelectItem>
                    <SelectItem value="departments">{T('أقسام محددة', 'Specific departments')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="anonymous-toggle" />
                <Label className="mb-0">{T('الإجابات مجهولة', 'Anonymous responses')}</Label>
              </div>
              <div className="md:col-span-2 border-t border-[#C4BFF0]/50 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Switch
                    checked={confidentialityNotice}
                    onCheckedChange={setConfidentialityNotice}
                    data-testid="confidentiality-toggle"
                  />
                  <Label className="mb-0 font-semibold text-[#655dc6]">
                    <Lock size={14} weight="fill" className="inline me-1" />
                    {T('إرفاق إشعار السرية في أعلى الاستبيان', 'Attach a confidentiality notice at the top of the survey')}
                  </Label>
                </div>
                {confidentialityNotice && (
                  <div className="ps-10 p-3 bg-blue-50 dark:bg-blue-900/20 border-s-4 border-blue-500 rounded text-xs text-blue-900 dark:text-blue-200 leading-relaxed" data-testid="confidentiality-preview">
                    <div className="font-semibold mb-1">
                      {T('🔒 إشعار السرية', '🔒 Confidentiality Notice')}
                    </div>
                    <div>
                      {T(
                        'مشاركتك في هذا الاستبيان سريّة بالكامل. إجاباتك لا تُربط باسمك أو ببياناتك الشخصية بأي شكل، وتُستخدم حصريًا من قبل إدارة الجودة لأغراض التحليل وتحسين بيئة العمل. نُثمّن صراحتك ومشاركتك الصادقة.',
                        'Your participation in this survey is strictly confidential. Your responses are not linked to your name or any personal information in any way, and are used solely by the Quality Department for analysis and workplace improvement purposes. We value your honesty and candid participation.',
                      )}
                    </div>
                  </div>
                )}
              </div>
              {audienceMode === 'departments' && (
                <div className="md:col-span-2">
                  <Label>{T('اختر الأقسام', 'Select departments')}</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {departments.length === 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {T('لا توجد أقسام مُعرّفة. يمكنك إضافتها من صفحة الإعدادات.', 'No departments defined. Add them from the Settings page.')}
                      </div>
                    )}
                    {departments.map((d) => {
                      const value = d.value || d.label_ar || d.label_en;
                      const label = isAr
                        ? (d.label_ar || d.label_en || d.value)
                        : (d.label_en || d.label_ar || d.value);
                      const active = audienceDepts.includes(value);
                      return (
                        <Button
                          key={d.id || value}
                          type="button"
                          size="sm"
                          variant={active ? 'default' : 'outline'}
                          onClick={() => setAudienceDepts((p) => active ? p.filter((x) => x !== value) : [...p, value])}
                          className={active ? 'bg-[#655dc6]' : ''}
                          data-testid={`survey-dept-btn-${value}`}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">{T('الأسئلة', 'Questions')}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addQuestion}>
                  <Plus size={14} className="me-1" /> {T('سؤال', 'Question')}
                </Button>
              </div>
              {questions.map((q, idx) => (
                <Card key={q.id} className="mb-2 border-dashed border-[#C4BFF0]">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-[#655dc6]/60 font-mono">#{idx + 1}</span>
                      <Select value={q.type} onValueChange={(v) => updateQuestion(idx, { type: v })}>
                        <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Q_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{T(t.label_ar, t.label_en)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1 ms-auto">
                        <Switch checked={q.required} onCheckedChange={(v) => updateQuestion(idx, { required: v })} />
                        <span className="text-[10px] text-[#655dc6]/70">{T('مطلوب', 'Req')}</span>
                      </div>
                      {questions.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeQuestion(idx)}>
                          <Trash size={14} className="text-rose-600" />
                        </Button>
                      )}
                    </div>
                    <Input
                      value={q.title_ar}
                      onChange={(e) => updateQuestion(idx, { title_ar: e.target.value })}
                      placeholder={T('نص السؤال بالعربي', 'Question text (AR)')}
                      data-testid={`q-title-${idx}`}
                    />
                    {q.type === 'single_choice' && (
                      <div className="space-y-1">
                        {(q.options || []).map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <Input
                              value={opt}
                              onChange={(e) => {
                                const copy = [...(q.options || [])];
                                copy[oi] = e.target.value;
                                updateQuestion(idx, { options: copy });
                              }}
                              placeholder={T(`الخيار ${oi + 1}`, `Option ${oi + 1}`)}
                              className="h-8"
                            />
                            {(q.options || []).length > 2 && (
                              <Button type="button" size="sm" variant="ghost" onClick={() => {
                                const copy = (q.options || []).filter((_, k) => k !== oi);
                                updateQuestion(idx, { options: copy });
                              }}>
                                <Trash size={12} className="text-rose-500" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="ghost" onClick={() => updateQuestion(idx, { options: [...(q.options || []), ''] })}>
                          <Plus size={12} /> {T('خيار', 'Option')}
                        </Button>
                      </div>
                    )}
                    {q.type === 'rating' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">{T('أقصى تقييم', 'Max rating')}:</Label>
                        <Input
                          type="number" min={3} max={10}
                          value={q.scale} className="w-20 h-8"
                          onChange={(e) => updateQuestion(idx, { scale: Number(e.target.value) })}
                        />
                        <div className="flex gap-0.5">
                          {Array.from({ length: q.scale }).map((_, i) => (
                            <Star key={i} size={14} weight="fill" className="text-amber-400" />
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetBuilder(); }}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={saveSurvey}
              disabled={saving}
              className="bg-[#655dc6] hover:bg-[#564db5] text-white"
              data-testid="save-survey-btn"
            >
              <CheckCircle size={16} weight="bold" className="me-1" />
              {saving
                ? T('حفظ...', 'Saving...')
                : (editingId
                    ? T('حفظ التعديلات', 'Save Changes')
                    : T('حفظ كمسودة', 'Save as Draft'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
}
